import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, type MiniappSeason, type MiniappTheme } from '@/lib/api';
import { haptic } from '@/lib/telegram';
import { AccentPicker } from '@/components/AccentPicker';
import { ThemePicker } from '@/components/bot-settings/theme-picker';
import { SeasonPicker } from '@/components/bot-settings/season-picker';
import { MiniAppLive } from '@/components/bot-settings/miniapp-live';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import {
  BannersCard,
  Field,
  PremiumEmojiCard,
  ServerNamesCard,
  StickersCard,
} from '@/components/bot-settings/cards';
import {
  LoadError,
  NoBots,
  ScreenHeader,
  Section,
  useBackToHub,
  useSaveButton,
  useSettingsBot,
} from '@/components/bot-settings/screen';

/** Легенда типов серверов, как её подставляет бэк под описанием подписки. Только для
 * превью: настоящий текст живёт в `public_subscription._DEFAULT_SERVER_LEGEND`. */
const SERVER_LEGEND_PREVIEW =
  '🛡️ — весь трафик идёт через VPN без роутинга\nBridge — мост: сначала на русский сервер, потом за рубеж\n⚡️ — обход белых списков\n📱 — постквантовое шифрование (чаще только на телефонах)';

// Уровни лояльности за продления (синхронно с backend MILESTONES).
const LOYALTY_LEVELS = [
  { count: '3', emoji: '🥉', def: 'Бронзовая подписка' },
  { count: '6', emoji: '🥈', def: 'Серебряная подписка' },
  { count: '12', emoji: '🥇', def: 'Золотая подписка' },
] as const;

const INPUT =
  'w-full surface-inset rounded-[13px] px-4 py-3 text-[16px] focus:outline-none focus:border-white/30';

/**
 * «Оформление»: как бот и подписка выглядят у клиента. Имя, цвет мини-аппа, баннеры,
 * профиль подписки в VPN-приложении, имена серверов, уровни, стикеры, премиум-эмодзи.
 * Как раздел «Оформление» в кабинете на сайте (`vpn/app/bot.brand`).
 */
export const Route = createFileRoute('/bot/brand')({
  component: BrandScreen,
  validateSearch: (s: Record<string, unknown>): { bot?: string } =>
    typeof s.bot === 'string' ? { bot: s.bot } : {},
});

type Form = {
  title: string;
  subTitle: string;
  subAnnounce: string;
  legend: boolean;
  theme: MiniappTheme;
  seasons: MiniappSeason[];
  loyalty: Record<string, string>;
};

function BrandScreen() {
  const { bot: botParam } = Route.useSearch();
  const { bot, isPending, isError, refetch } = useSettingsBot(botParam);
  useBackToHub();

  if (isPending) return <BrandSkeleton />;
  if (isError) return <LoadError onRetry={refetch} />;
  if (!bot) return <NoBots />;
  return <BrandForm key={bot.id} botId={bot.id} />;
}

function BrandForm({ botId }: { botId: string }) {
  const qc = useQueryClient();
  const { data: detail, isPending, isError, refetch } = useQuery({
    queryKey: ['bot', botId],
    queryFn: () => api.getBot(botId),
    retry: 1,
  });
  const { data: bots = [] } = useQuery({ queryKey: ['bots'], queryFn: () => api.bots(), staleTime: 60_000 });
  const listBot = bots.find((b) => b.id === botId);

  const [form, setForm] = useState<Form | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  // Сезон в превью: undefined, пока партнёр ничего не смотрел (тогда тот, что идёт сегодня).
  const [previewSeason, setPreviewSeason] = useState<MiniappSeason | null | undefined>(undefined);

  // Форма заливается из данных один раз: тумблеры на этом же экране (премиум-эмодзи)
  // обновляют кэш бота, и перезаливка стёрла бы набранное.
  useEffect(() => {
    if (!detail || form) return;
    const next: Form = {
      title: detail.title || '',
      subTitle: detail.sub_profile_title || '',
      subAnnounce: detail.sub_announce || '',
      legend: detail.sub_announce_legend !== false,
      theme: detail.miniapp_theme ?? 'graphite',
      seasons: detail.miniapp_seasons ?? [],
      loyalty: detail.loyalty_titles || {},
    };
    setForm(next);
    setSaved(JSON.stringify(next));
  }, [detail, form]);

  // Имена серверов сохраняются своим запросом, но той же кнопкой.
  const savers = useRef(new Map<string, () => Promise<unknown>>());
  const [pendingSections, setPendingSections] = useState(0);
  const registerSaver = useCallback((key: string, fn: (() => Promise<unknown>) | null) => {
    if (fn) savers.current.set(key, fn);
    else savers.current.delete(key);
    setPendingSections(savers.current.size);
  }, []);

  const formDirty = form !== null && saved !== null && JSON.stringify(form) !== saved;
  const dirty = formDirty || pendingSections > 0;

  const { mutate: save, isPending: saving } = useMutation({
    // Отправленный снимок: что набрали во время запроса, остаётся несохранённым.
    mutationFn: async (sent: Form) => {
      if (JSON.stringify(sent) !== saved) {
        const updated = await api.updateBot(botId, {
          title: sent.title.trim() || undefined,
          sub_profile_title: sent.subTitle,
          sub_announce: sent.subAnnounce,
          sub_announce_legend: sent.legend,
          miniapp_theme: sent.theme,
          miniapp_seasons: sent.seasons,
          // Пустые названия отбросит бэк (сброс к стандартному).
          loyalty_titles: Object.fromEntries(Object.entries(sent.loyalty).filter(([, v]) => v.trim())),
        });
        qc.setQueryData(['bot', botId], (prev: typeof detail) => prev && { ...prev, ...updated });
      }
      await Promise.all([...savers.current.values()].map((fn) => fn()));
    },
    onSuccess: (_r, sent) => {
      setSaved(JSON.stringify(sent));
      void qc.invalidateQueries({ queryKey: ['bots'] });
      haptic('success');
      toast.success('Сохранено');
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось. Попробуйте ещё раз.');
    },
  });

  useSaveButton({ dirty, saving, onSave: () => form && save(form) });

  if (isError) return <LoadError onRetry={() => void refetch()} />;
  if (isPending || !detail || !form || !listBot) return <BrandSkeleton />;

  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const shownSeason =
    previewSeason !== undefined
      ? previewSeason
      : (detail.season_schedule ?? []).find((r) => r.showing && form.seasons.includes(r.season))?.season ?? null;
  const live = (
    <MiniAppLive
      src={detail.miniapp_preview_url ?? null}
      look={{
        theme: form.theme,
        color: detail.primary_color || '#3390ec',
        title: form.title.trim(),
        logo: null,
        season: shownSeason,
      }}
      className="mt-4"
    />
  );
  const announce = [form.subAnnounce.trim(), form.legend ? SERVER_LEGEND_PREVIEW : ''].filter(Boolean).join('\n\n');

  return (
    <div className="p-4 pb-32 space-y-6">
      <ScreenHeader title="Оформление" bot={listBot} />

      <Section id="bot" title="Название бота" hint="Видно клиентам в шапке мини-аппа. Это просто имя, не адрес.">
        <input
          value={form.title}
          onChange={(e) => set({ title: e.target.value })}
          maxLength={100}
          aria-label="Название бота"
          className={INPUT}
        />
      </Section>

      <Section id="brand" title="Цвет мини-аппа">
        <AccentPicker botId={botId} />
      </Section>

      <Section
        id="theme"
        title="Тема мини-аппа"
        hint="Как это выглядит у клиента, видно на телефоне под сезонами. Цвет кнопок остаётся вашим."
      >
        <ThemePicker value={form.theme} onChange={(theme) => set({ theme })} />
        {!detail.season_schedule && live}
      </Section>

      {detail.season_schedule && (
        <Section
          id="seasons"
          title="Сезонное оформление"
          hint="Листопад, снег и праздники поверх темы. Цвет и раскладка остаются вашими."
        >
          <SeasonPicker
            value={form.seasons}
            schedule={detail.season_schedule}
            onChange={(seasons) => set({ seasons })}
            previewing={shownSeason}
            onPreview={setPreviewSeason}
          />
          {/* Телефон под темой и сезонами: на узком экране оба выбора видны рядом с ним. */}
          {live}
        </Section>
      )}

      <Section id="premium-emoji" title="Иконки бота">
        <PremiumEmojiCard
          botId={botId}
          initial={detail.premium_emoji_enabled}
          initialColor={detail.icon_color ?? null}
          available={detail.icon_colors_available}
        />
      </Section>

      <Section id="stickers" title="Стикеры событий">
        <StickersCard botId={botId} stickers={detail.stickers || {}} />
      </Section>

      <Section id="banners" title="Картинки экранов" hint="Картинка над сообщением бота. Без своей экран берёт картинку главного меню. JPG, PNG или WEBP до 5 МБ.">
        <BannersCard botId={botId} banners={detail.banners || {}} />
      </Section>

      <Section id="vpn-app" title="Подписка в VPN-приложении">
        <div className="space-y-4">
          <p className="text-[13px] leading-snug text-muted-foreground">
            Так подписка выглядит в Happ, V2RayTun и похожих приложениях.
          </p>
          <div aria-hidden className="select-none rounded-[14px] border border-white/10 bg-black/40 p-3">
            <div className="flex items-center gap-2">
              <ChevronRight className="size-4 shrink-0 rotate-90 text-muted-foreground" />
              <span className="truncate text-sm font-semibold">{form.subTitle.trim() || detail.title || 'VPN'}</span>
              <span className="ml-auto whitespace-nowrap text-[10px] text-muted-foreground">Автообновление · 1ч</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                <div className="h-full w-1/3 bg-white/40" />
              </div>
              <span className="whitespace-nowrap">4.17 ГБ / ∞</span>
            </div>
            {announce && (
              <p className="mt-2 whitespace-pre-line break-words text-[11px] leading-snug text-foreground/80">{announce}</p>
            )}
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-foreground">
              <MessageCircle className="size-3" /> Поддержка
            </div>
          </div>
          <Field
            label="Заголовок"
            hint="Вместо «VPN». Пусто: имя бота. Happ показывает около 25 символов."
          >
            <input
              value={form.subTitle}
              onChange={(e) => set({ subTitle: e.target.value })}
              maxLength={64}
              placeholder={detail.title || 'VPN'}
              className={INPUT}
            />
          </Field>
          <Field label="Описание" hint="Короткая строка в карточке, до 200 символов. Часть приложений её не показывает.">
            <textarea
              value={form.subAnnounce}
              onChange={(e) => set({ subAnnounce: e.target.value })}
              rows={2}
              maxLength={200}
              placeholder="⚡ Быстрый и анонимный VPN"
              className={`${INPUT} resize-none`}
            />
          </Field>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={form.legend}
              onChange={(e) => set({ legend: e.target.checked })}
              className="mt-0.5 size-4 shrink-0 accent-foreground"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium">Легенда типов серверов под описанием</span>
              <span className="mt-0.5 block text-[12px] text-muted-foreground">
                Что значат молния, мост и щит. Выключите, чтобы осталось только ваше описание.
              </span>
            </span>
          </label>
        </div>
      </Section>

      <Section id="servers" title="Имена серверов">
        <ServerNamesCard botId={botId} registerSaver={registerSaver} />
      </Section>

      <Section id="loyalty" title="Уровни за продления" hint="Видно клиенту в профиле. Пусто: стандартное название.">
        <div className="space-y-3">
          {LOYALTY_LEVELS.map((lvl) => (
            <Field key={lvl.count} label={`${lvl.emoji} После ${lvl.count} продлений`}>
              <input
                value={form.loyalty[lvl.count] ?? ''}
                onChange={(e) => set({ loyalty: { ...form.loyalty, [lvl.count]: e.target.value } })}
                maxLength={64}
                placeholder={lvl.def}
                className={INPUT}
              />
            </Field>
          ))}
        </div>
      </Section>
    </div>
  );
}

function BrandSkeleton() {
  return (
    <SkeletonPage className="p-4 pb-32 space-y-6">
      <div className="px-1 pt-3">
        <h1 className="font-display text-[22px] font-medium">Оформление</h1>
        <SkeletonLine size="sm" className="mt-1.5 w-40" />
      </div>
      {[52, 120, 220, 320].map((h, i) => (
        <section key={i}>
          <Skeleton className="mb-2 ml-1 h-3 w-28 rounded" />
          <SkeletonCard className="rounded-[20px]" style={{ minHeight: h }} />
        </section>
      ))}
    </SkeletonPage>
  );
}
